/**
 * matrix-utils - Utility functions for Matrix Mode
 *
 * Shared helpers for creating matrix items and loading product data.
 */

import type { BatchMode, MatrixItem } from '@/lib/api/batch';
import { getAIReferenceImages } from '@/lib/api/products';

export interface GlobalMatrixSettings {
  mode: BatchMode;
  templateId: string;
  collectionId: string;
  customPrompt: string;
  aspectRatios: string[];
  variants: number;
  seedMode: 'auto' | 'fixed';
}

export const DEFAULT_GLOBAL_SETTINGS: GlobalMatrixSettings = {
  mode: 'template',
  templateId: '',
  collectionId: '',
  customPrompt: '',
  aspectRatios: ['1:1'],
  variants: 1,
  seedMode: 'auto',
};

export function createMatrixItem(
  defaultTemplateId?: string,
  globalSettings?: Partial<GlobalMatrixSettings>,
): MatrixItem {
  const mode = globalSettings?.mode || 'template';
  return {
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    input_type: 'upload',
    mode,
    template_id: mode === 'template' ? (globalSettings?.templateId || defaultTemplateId || '') : undefined,
    collection_id: mode === 'collection' ? (globalSettings?.collectionId || '') : undefined,
    custom_prompt: mode === 'prompt' ? (globalSettings?.customPrompt || '') : undefined,
    aspect_ratios: globalSettings?.aspectRatios || ['1:1'],
    variants: globalSettings?.variants || 1,
    seed: globalSettings?.seedMode === 'fixed' ? 12345 : null,
  };
}

export async function loadProductMatrixItems(
  productIds: string[],
  productNames?: Record<string, string>,
): Promise<MatrixItem[]> {
  const items: MatrixItem[] = [];

  const results = await Promise.allSettled(
    productIds.map((id) => getAIReferenceImages(id)),
  );

  for (let i = 0; i < productIds.length; i++) {
    const productId = productIds[i];
    const result = results[i];
    const name = productNames?.[productId] || `Produkt ${i + 1}`;

    let assetId: string | undefined;
    let thumbnailUrl: string | undefined;

    if (result.status === 'fulfilled' && result.value.slots) {
      const packshot = result.value.slots.find(
        (s) => s.role === 'PACKSHOT' && !s.is_empty && s.asset_id,
      );
      if (packshot) {
        assetId = packshot.asset_id;
        thumbnailUrl = packshot.thumbnail_url;
      }
    }

    items.push({
      id: `row-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
      input_type: assetId ? 'asset' : 'upload',
      asset_id: assetId,
      product_id: productId,
      product_name: name,
      _thumbnailUrl: thumbnailUrl,
      template_id: '',
      aspect_ratios: ['1:1'],
      variants: 1,
    });
  }

  return items;
}
